import React, {useCallback, useEffect, useRef, useState} from 'react';
import {animated, useSpring} from '@react-spring/web';
import {FederatedPointerEvent, DisplayObject, Rectangle, TextStyle, Texture} from 'pixi.js';
import {Container, Sprite, Text} from '@pixi/react';

const Card = ({
  index,
  stageSize,
  image,
  text,
  textAnchor,
  textStyle,
  selected,
  onDragStart,
  onClick,
}: {
  index: number;
  stageSize: [number, number];
  image?: string | number;
  text?: string | null;
  textAnchor?: [number, number];
  textStyle?: TextStyle;
  selected: boolean;
  onDragStart: () => void;
  onClick: () => void;
}) => {
  if (index > maxZIndex) maxZIndex = index;

  const cardScaleRef = useRef(0);
  const stageBoundsRef = useRef(Rectangle.EMPTY);
  const startPosRef = useRef([-1, -1] as [number, number]);
  const wasSelectedRef = useRef(false);

  const [zIndex, setZIndex] = useState(index);
  const [pointerDownT, setPointerDownT] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [motion, anim] = useSpring(() => ({
    from: {position: [-1, -1] as [number, number], scale: 0, angle: 0},
  }));

  const onPointerDown = useCallback(() => {
    setPointerDownT(Date.now());
    setZIndex(incMaxZIndex());
  }, []);

  const onPointerMove = useCallback(
    (event: FederatedPointerEvent) => {
      if (!pointerDownT) return;

      if (!isDragging && Date.now() - pointerDownT > 200) {
        anim.start({to: {angle: 0}});
        setIsDragging(true);
        onDragStart();
      }

      const parent = event.currentTarget.parent?.parent;
      if (parent) {
        const newPos = event.getLocalPosition(parent as DisplayObject);
        anim.set({position: [newPos.x, newPos.y]});
      }
    },
    [anim, isDragging, onDragStart, pointerDownT],
  );

  const onPointerUp = useCallback(() => {
    const stageBounds = stageBoundsRef.current;
    if (isDragging) {
      const pEnd = motion.position.get();
      const position: [number, number] = [
        clamp(pEnd[0], stageBounds.left, stageBounds.right),
        clamp(pEnd[1], stageBounds.top, stageBounds.bottom),
      ];
      anim.start({to: {position, angle: randAngle()}});
      startPosRef.current = position;
    }
    setPointerDownT(0);
  }, [anim, isDragging, motion]);

  const onPointerTap = useCallback(() => {
    if (isDragging) setIsDragging(false);
    else onClick();
  }, [isDragging, onClick]);

  useEffect(() => {
    cardScaleRef.current = getCardScale(stageSize);
    stageBoundsRef.current = getStageBounds(stageSize);

    if (startPosRef.current[0] < 0) {
      startPosRef.current = randPos(stageBoundsRef.current);
      anim.set({position: startPosRef.current, scale: cardScaleRef.current, angle: randAngle()});
    }
  }, [anim, stageSize]);

  useEffect(() => {
    const stageBounds = stageBoundsRef.current;
    const cardScale = cardScaleRef.current;
    const startPos = startPosRef.current;
    const wasSelected = wasSelectedRef.current;

    if (selected) {
      setZIndex(incMaxZIndex());
      const position: [number, number] = [
        stageBounds.x + 0.5 * stageBounds.width,
        stageBounds.y + 0.25 * stageBounds.height,
      ];
      anim.start({to: {position, scale: 2 * cardScale, angle: 0}});
      wasSelectedRef.current = true;
    } else if (wasSelected) {
      anim.start({to: {position: startPos, scale: cardScale, angle: randAngle()}});
    }
  }, [anim, selected]);

  return (
    <AnimatedContainer zIndex={zIndex} {...motion} eventMode="passive">
      <Sprite
        image={'assets/card_shadow.png'}
        anchor={0.5}
        width={CardSize[0] + 90}
        height={CardSize[1] + 90}
        eventMode="none"
      />
      <Sprite
        texture={typeof image === 'string' ? undefined : Texture.WHITE}
        image={typeof image === 'string' ? image : undefined}
        tint={typeof image === 'number' ? image : 0xffffff}
        anchor={0.5}
        width={CardSize[0]}
        height={CardSize[1]}
        eventMode="static"
        pointerdown={onPointerDown}
        pointermove={onPointerMove}
        pointerup={onPointerUp}
        pointerupoutside={onPointerUp}
        pointertap={onPointerTap}
        cursor={isDragging ? 'move' : 'pointer'}
      />
      {text && textAnchor && textStyle && (
        <Text text={text} anchor={textAnchor} style={textStyle} eventMode="none" />
      )}
    </AnimatedContainer>
  );
};
export default Card;

const AnimatedContainer = animated(Container);

let maxZIndex = 0;
const incMaxZIndex = () => ++maxZIndex;

const randAngle = () => 90 * Math.random() - 45;

export const CardSize = [900, 600] as const;

const getCardScale = (stageSize: [number, number]) => {
  const [W, H] = stageSize;
  return W < H ? W / 4 / CardSize[0] : H / 4 / CardSize[1];
};

const getStageBounds = (stageSize: [number, number]) => {
  const [W, H] = stageSize;
  const s = getCardScale(stageSize);
  const [cw, ch] = [s * CardSize[0], s * CardSize[1]];
  return new Rectangle(0.5 * cw, 0.5 * ch, W - cw, H - ch);
};

const randPos = (stageBounds: Rectangle): [number, number] => {
  return [
    stageBounds.x + stageBounds.width * Math.random(),
    stageBounds.y + stageBounds.height * Math.random(),
  ];
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(min, value), max);
